import axios, { type InternalAxiosRequestConfig } from 'axios';
import { keycloak, saveTokens, clearTokens, getStoredToken, isE2ETokenAuthEnabled } from '../auth';

type AuthRetryConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
};

// ВАЖНО: НЕ использовать '/api' как fallback - это будет относительно текущего домена!
// Mobile frontend (MOBILE_WEB_URL в .env) - это фронтенд, API на api.doctor-server.com
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.doctor-server.com/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const e2eToken = isE2ETokenAuthEnabled() ? getStoredToken() : null;
    let authToken = isE2ETokenAuthEnabled() ? e2eToken : keycloak.token;
    if (authToken) {
      try {
        if (!isE2ETokenAuthEnabled() && keycloak.token) {
          const refreshed = await keycloak.updateToken(30);
          if (refreshed) saveTokens();
          authToken = keycloak.token || authToken;
        }
      } catch {
        // One retry after 1s delay
        try {
          await new Promise((r) => setTimeout(r, 1000));
          if (!isE2ETokenAuthEnabled() && keycloak.token) {
            const refreshed = await keycloak.updateToken(5);
            if (refreshed) saveTokens();
            authToken = keycloak.token || authToken;
          }
        } catch {
          clearTokens();
          if (!isE2ETokenAuthEnabled()) keycloak.login();
          return Promise.reject(new Error('Token refresh failed'));
        }
      }

      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      if (isE2ETokenAuthEnabled()) {
        clearTokens();
        return Promise.reject(error);
      }
      const originalConfig = error.config as AuthRetryConfig | undefined;
      // Skip vault endpoints — their 401 means wrong vault password, not expired Keycloak session
      const url = originalConfig?.url || '';
      const isVaultEndpoint = url.includes('/vault/');

      if (!isVaultEndpoint && originalConfig && !originalConfig._authRetry) {
        try {
          originalConfig._authRetry = true;
          await keycloak.updateToken(-1); // force refresh
          saveTokens();
          originalConfig.headers.Authorization = `Bearer ${keycloak.token}`;
          return api(originalConfig);
        } catch {
          clearTokens();
          keycloak.login();
        }
      }
      return Promise.reject(error);
    }

    if (import.meta.env.DEV) {
      console.error('API Error:', error.response?.data || error.message);
    }
    return Promise.reject(error);
  },
);
